import create from 'zustand';
import { loadFromLocalStorage, saveToLocalStorage } from '../utils/localStorage';

type Button = {
  name: string;
  value: string;
  displayName: string;
};
type AccordionStore = {
  expandedAccordions: string[]; // Support multiple expanded accordions
  activeButtons: Record<string, Button>; // Track active buttons per accordion
  setExpandedAccordion: (panel: string, isExpanded: boolean, buttons?: Button[]) => void;
  setSelectedButton: (bargeName: string, button: Button) => void;
  initializeAccordion: (
    accordionGroup: Array<{
      name: string;
      buttons: Button[];
    }>,
  ) => void;
  resetAccordion: () => void;
};

export const useAccordionStore = create<AccordionStore>((set, get) => ({
  expandedAccordions: loadFromLocalStorage('expandedAccordions') || [],
  activeButtons: loadFromLocalStorage('activeButtons') || {},

  setExpandedAccordion: (panel, isExpanded, buttons) => {
    const { activeButtons } = get();
    //TODO: Logic to handle multiple expanded accordions, uncomment if needed
    // const newExpandedAccordions = isExpanded
    //   ? expandedAccordions.includes(panel)
    //     ? expandedAccordions // Already exists; no need to add again
    //     : [...expandedAccordions, panel] // Add the panel
    //   : expandedAccordions.filter((item) => item !== panel); // Remove the panel
    const newExpandedAccordions = isExpanded ? [panel] : [];

    if (isExpanded && buttons && !activeButtons[panel]) {
      set({
        activeButtons: {
          ...activeButtons,
          [panel]: {
            name: buttons[0].name,
            value: buttons[0].value,
            displayName: buttons[0].displayName,
          },
        },
      });
    }

    set({
      expandedAccordions: newExpandedAccordions,
    });

    saveToLocalStorage('expandedAccordions', newExpandedAccordions);
    saveToLocalStorage('activeButtons', get().activeButtons);
  },

  setSelectedButton: (panel, button) => {
    const { expandedAccordions } = get();

    // Check if the panel is already expanded
    if (!expandedAccordions.includes(panel)) {
      set({
        expandedAccordions: [panel],
      });
      // TODO: Logic to handle multiple expanded accordions, uncomment if needed
      //saveToLocalStorage('expandedAccordions', [...expandedAccordions, panel]);
      saveToLocalStorage('expandedAccordions', [panel]);
    }

    // Update the active button for the selected barge
    set({
      activeButtons: {
        // Support multiple expanded accordions
        // ...get().activeButtons,
        [panel]: button,
      },
    });
    // Save active buttons to local storage
    saveToLocalStorage('activeButtons', get().activeButtons);
  },

  initializeAccordion: (accordionGroup) => {
    const { expandedAccordions, activeButtons } = get();

    if (expandedAccordions.length && activeButtons) {
      // Restore previous session's selection
      set({
        expandedAccordions,
        activeButtons,
      });
    } else if (accordionGroup.length === 1) {
      // Automatically expand if only one accordion and set initial button
      const panel = accordionGroup[0].name;
      set({
        expandedAccordions: [panel],
        activeButtons: {
          [panel]: accordionGroup[0].buttons[0],
        },
      });

      saveToLocalStorage('expandedAccordions', [panel]);
      saveToLocalStorage('activeButtons', get().activeButtons);

    } else {
      // Multiple accordions, no default expansion
      set({
        expandedAccordions: [],
        activeButtons: {},
      });
    }
  },

  resetAccordion: () => {
    set({ expandedAccordions: [] });
    saveToLocalStorage('expandedAccordions', []);
  },
}));
